// src/pages/Billing.jsx — Complete Safepay version
// Replace your existing Billing.jsx with this

import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useCompanyPlan, TEST_MODE } from '../hooks/useCompanyPlan';
import Layout from '../components/Layout';
import { getPlan, isHigherPlan } from '../lib/planFeatures';
import {
  CheckCircle, X, Check, ArrowRight,
  Clock, Star, Zap, Crown, AlertCircle,
} from 'lucide-react';

const PLAN_ORDER = ['trial', 'basic', 'pro', 'enterprise'];

export default function Billing() {
  const { company, refreshCompany } = useAuth();
  const {
    plan, can, currentPlan,
    upgrading, upgradeError,
    upgradePlan, verifyPayment, cancelPlan,
    testMode,
  } = useCompanyPlan(company, refreshCompany);

  const [cycle,      setCycle]      = useState('monthly');
  const [confirming, setConfirming] = useState(null);
  const [success,    setSuccess]    = useState('');
  const [verifying,  setVerifying]  = useState(false);
  const [cancelConf, setCancelConf] = useState(false);

  // ── Handle Safepay redirect back ─────────────────────
  // After company pays on Safepay and comes back to /billing
  // URL will have ?tracker=xxx&plan=basic&cycle=monthly&status=success
  useEffect(() => {
    if (!company) return;

    const params  = new URLSearchParams(window.location.search);
    const tracker = params.get('tracker');
    const status  = params.get('status');
    const pPlan   = params.get('plan')  || sessionStorage.getItem('sp_plan');
    const pCycle  = params.get('cycle') || sessionStorage.getItem('sp_cycle');

    // Clear URL immediately so refresh doesn't re-trigger
    if (params.has('tracker') || params.has('status')) {
      window.history.replaceState({}, '', '/billing');
    }

    if (tracker && status === 'success' && pPlan && pCycle) {
      setVerifying(true);
      setSuccess('Verifying your payment with Safepay...');

      verifyPayment(tracker, pPlan, pCycle).then(result => {
        setVerifying(false);
        if (result.success) {
          setSuccess(
            `✅ Successfully upgraded to ${getPlan(pPlan).name}! All features are now active.`
          );
          setTimeout(() => setSuccess(''), 10000);
        } else {
          setSuccess('');
          setSuccess('');
          // Show error but also show support info
          setSuccess(
            `Payment received but verification failed. ` +
            `Please contact support@inncatechnologies.com with your payment reference.`
          );
        }
      });
      return;
    }

    if (status === 'cancelled') {
      setSuccess('');
    }
  }, [company]);

  const handleUpgrade = (targetPlan) => {
    if (targetPlan === currentPlan) return;
    if (targetPlan === 'trial') return;
    setConfirming(targetPlan);
  };

  const confirmUpgrade = async () => {
    if (!confirming) return;
    const result = await upgradePlan(confirming, cycle);

    // In test mode success comes back immediately
    if (result?.success && result?.testMode) {
      setConfirming(null);
      setSuccess(
        `✅ Plan upgraded to ${getPlan(confirming).name} (Test Mode — no real payment).`
      );
      setTimeout(() => setSuccess(''), 6000);
    }
    // In production, page redirects to Safepay so we won't reach here
    // If there's an error though:
    if (!result?.success && !result?.redirecting) {
      setConfirming(null);
    }
  };

  const handleCancel = async () => {
    const result = await cancelPlan();
    if (result.success) {
      setCancelConf(false);
      setSuccess('Plan cancelled. You keep access until end of billing period.');
      setTimeout(() => setSuccess(''), 7000);
    }
  };

  const PLAN_ICONS = {
    trial:      <Clock size={18} />,
    basic:      <Star size={18} />,
    pro:        <Zap size={18} />,
    enterprise: <Crown size={18} />,
  };

  return (
    <Layout>
      <div style={{ maxWidth: 920, margin: '0 auto', padding: '0 16px 60px' }}>

        {/* ── TEST MODE BANNER ─────────────────────────── */}
        {TEST_MODE && (
          <div style={{
            background: '#FFFBEB',
            border: '1.5px solid #FDE68A',
            borderRadius: 14,
            padding: '14px 20px',
            marginBottom: 24,
            display: 'flex',
            gap: 14,
            alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: 22 }}>🧪</span>
            <div>
              <div style={{ fontWeight: 800, color: '#92400E', fontSize: 13 }}>
                Test Mode — No Real Payments
              </div>
              <div style={{ color: '#78350F', fontSize: 12, marginTop: 3, lineHeight: 1.6 }}>
                Clicking Upgrade will instantly activate the plan without payment.
                To enable real Safepay payments, set{' '}
                <code style={{
                  background: '#FDE68A', padding: '1px 6px',
                  borderRadius: 4, fontSize: 11,
                }}>
                  TEST_MODE = false
                </code>{' '}
                in <code style={{
                  background: '#FDE68A', padding: '1px 6px',
                  borderRadius: 4, fontSize: 11,
                }}>src/hooks/useCompanyPlan.js</code>
              </div>
            </div>
          </div>
        )}

        {/* ── Header ──────────────────────────────────── */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1E293B', margin: 0 }}>
              Plans & Billing
            </h1>
            {TEST_MODE && (
              <span style={{
                background: '#F59E0B', color: '#fff',
                padding: '3px 10px', borderRadius: 6,
                fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
              }}>
                TEST MODE
              </span>
            )}
          </div>
          <p style={{ color: '#94A3B8', fontSize: 14, margin: 0 }}>
            {company?.name} · Current plan:{' '}
            <strong style={{ color: plan.color }}>{plan.name}</strong>
          </p>
        </div>

        {/* ── Verifying spinner ───────────────────────── */}
        {verifying && (
          <div style={{
            background: '#EFF6FF', border: '1.5px solid #BFDBFE',
            borderRadius: 14, padding: '16px 20px',
            marginBottom: 24, display: 'flex', gap: 12, alignItems: 'center',
          }}>
            <div style={{
              width: 20, height: 20, border: '3px solid #BFDBFE',
              borderTopColor: '#3B82F6', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
            <div style={{ fontWeight: 600, color: '#1D4ED8', fontSize: 14 }}>
              Verifying your payment with Safepay...
            </div>
          </div>
        )}

        {/* ── Success ─────────────────────────────────── */}
        {success && !verifying && (
          <div style={{
            background: '#F0FDF4', border: '1.5px solid #BBF7D0',
            borderRadius: 14, padding: '16px 20px',
            marginBottom: 24, display: 'flex', gap: 12, alignItems: 'flex-start',
          }}>
            <CheckCircle size={20} color="#10B981" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontWeight: 600, color: '#065F46', fontSize: 14, lineHeight: 1.5 }}>
              {success}
            </div>
          </div>
        )}

        {/* ── Error ───────────────────────────────────── */}
        {upgradeError && (
          <div style={{
            background: '#FEF2F2', border: '1.5px solid #FECACA',
            borderRadius: 14, padding: '14px 20px',
            marginBottom: 24, display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <AlertCircle size={18} color="#EF4444" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ color: '#991B1B', fontSize: 13, lineHeight: 1.5 }}>
              {upgradeError}
            </div>
          </div>
        )}

        {/* ── Current Plan Summary ─────────────────────── */}
        <div style={{
          background: '#fff', borderRadius: 18,
          border: `2px solid ${plan.colorBorder}`,
          padding: '20px 24px', marginBottom: 28,
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: plan.gradient,
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: '#fff', fontSize: 22,
            }}>
              {plan.badge}
            </div>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, color: plan.color,
                textTransform: 'uppercase', letterSpacing: 1,
              }}>
                Current Plan
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#1E293B' }}>
                {plan.name}
              </div>
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
                {plan.price?.monthly === 0
                  ? 'Free'
                  : `$${company?.billing_cycle === 'yearly'
                      ? (plan.price?.yearly / 12).toFixed(0)
                      : plan.price?.monthly}/mo · Billed ${company?.billing_cycle || 'monthly'}`}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{
              background: '#F8FAFF', border: '1px solid #E2E8F0',
              borderRadius: 12, padding: '10px 16px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: plan.color }}>
                {plan.jobSlots === -1 ? '∞' : `${company?.slots_used || 0}/${plan.jobSlots}`}
              </div>
              <div style={{ fontSize: 10, color: '#94A3B8' }}>Job Slots</div>
            </div>

            {company?.next_billing_date && (
              <div style={{
                background: '#F8FAFF', border: '1px solid #E2E8F0',
                borderRadius: 12, padding: '10px 16px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>
                  {new Date(company.next_billing_date).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric',
                  })}
                </div>
                <div style={{ fontSize: 10, color: '#94A3B8' }}>Next Renewal</div>
              </div>
            )}

            {currentPlan !== 'trial' && !company?.cancelled_at && (
              <button onClick={() => setCancelConf(true)} style={{
                padding: '8px 14px', borderRadius: 8,
                border: '1px solid #FECACA', background: '#FEF2F2',
                color: '#EF4444', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>
                Cancel Plan
              </button>
            )}

            {company?.cancelled_at && (
              <div style={{
                padding: '8px 14px', borderRadius: 8,
                background: '#FFF7ED', border: '1px solid #FED7AA',
                color: '#C2410C', fontSize: 11, fontWeight: 600,
              }}>
                ⚠️ Cancelled — active until{' '}
                {new Date(company.plan_expires_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        {/* ── Billing Cycle Toggle ─────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <div style={{
            background: '#F1F5F9', borderRadius: 12,
            padding: 4, display: 'inline-flex',
          }}>
            {['monthly', 'yearly'].map(c => (
              <button key={c} onClick={() => setCycle(c)} style={{
                padding: '8px 20px', borderRadius: 10, border: 'none',
                background: cycle === c ? '#fff' : 'transparent',
                color: cycle === c ? '#1E293B' : '#64748B',
                fontWeight: 700, fontSize: 13, cursor: 'pointer',
                boxShadow: cycle === c ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {c === 'monthly' ? 'Monthly' : (
                  <>
                    Yearly
                    <span style={{
                      background: 'linear-gradient(135deg,#10B981,#059669)',
                      color: '#fff', fontSize: 9, fontWeight: 800,
                      padding: '2px 6px', borderRadius: 4,
                    }}>SAVE 20%</span>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Plan Cards ───────────────────────────────── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: 16, marginBottom: 40,
        }}>
          {PLAN_ORDER.map(planKey => {
            const p         = getPlan(planKey);
            const isCurrent = currentPlan === planKey;
            const canUpgrade = isHigherPlan(currentPlan, planKey);
            const isTrial   = planKey === 'trial';
            const price     = cycle === 'yearly'
              ? (p.price?.yearly / 12).toFixed(0)
              : p.price?.monthly;

            return (
              <div key={planKey} style={{
                background: '#fff', borderRadius: 20,
                border: `2px solid ${isCurrent ? p.color : p.colorBorder}`,
                overflow: 'hidden', position: 'relative',
                boxShadow: isCurrent
                  ? `0 4px 20px ${p.colorBg}`
                  : '0 2px 12px rgba(0,0,0,0.04)',
              }}>
                {isCurrent && (
                  <div style={{
                    background: p.gradient,
                    textAlign: 'center', padding: '6px 0',
                    fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: 1,
                  }}>
                    ✓ YOUR PLAN
                  </div>
                )}

                <div style={{ padding: '20px 18px', paddingTop: isCurrent ? 14 : 20 }}>
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: p.gradient,
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: '#fff', fontSize: 17,
                    }}>
                      {p.badge}
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 15, color: '#1E293B' }}>
                        {p.name}
                      </div>
                    </div>
                  </div>

                  {/* Price */}
                  <div style={{ marginBottom: 16 }}>
                    {isTrial ? (
                      <div style={{ fontSize: 28, fontWeight: 900, color: '#1E293B' }}>Free</div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                          <span style={{ fontSize: 28, fontWeight: 900, color: '#1E293B' }}>
                            ${price}
                          </span>
                          <span style={{ fontSize: 12, color: '#94A3B8' }}>/mo</span>
                        </div>
                        {cycle === 'yearly' && (
                          <div style={{ fontSize: 11, color: '#10B981', fontWeight: 600 }}>
                            ${p.price?.yearly}/yr — save ${(p.price?.monthly * 12) - p.price?.yearly}/yr
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Features */}
                  <div style={{ marginBottom: 16 }}>
                    {(p.highlights || []).slice(0, 5).map((h, i) => (
                      <div key={i} style={{
                        display: 'flex', gap: 7, alignItems: 'flex-start',
                        padding: '3px 0',
                      }}>
                        <Check size={12} color={p.color} style={{ marginTop: 2, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: '#475569', lineHeight: 1.4 }}>{h}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  {isCurrent ? (
                    <div style={{
                      width: '100%', padding: '11px 0',
                      borderRadius: 10, background: p.colorBg,
                      border: `1.5px solid ${p.colorBorder}`,
                      color: p.color, fontWeight: 700, fontSize: 13,
                      textAlign: 'center',
                    }}>
                      ✓ Current Plan
                    </div>
                  ) : canUpgrade ? (
                    <button
                      onClick={() => handleUpgrade(planKey)}
                      disabled={upgrading}
                      style={{
                        width: '100%', padding: '11px 0',
                        borderRadius: 10, border: 'none',
                        background: upgrading ? '#E2E8F0' : p.gradient,
                        color: upgrading ? '#94A3B8' : '#fff',
                        fontWeight: 700, fontSize: 13,
                        cursor: upgrading ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 6,
                      }}>
                      {upgrading && confirming === planKey
                        ? (TEST_MODE ? 'Activating...' : 'Redirecting...')
                        : `${TEST_MODE ? '⚡ ' : ''}Upgrade to ${p.name}`}
                      {!upgrading && <ArrowRight size={14} />}
                    </button>
                  ) : isTrial ? (
                    <div style={{
                      width: '100%', padding: '11px 0', borderRadius: 10,
                      border: '1px solid #E2E8F0', color: '#CBD5E1',
                      fontWeight: 600, fontSize: 13, textAlign: 'center',
                    }}>
                      Free Plan
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Safepay info ─────────────────────────────── */}
        {!TEST_MODE && (
          <div style={{
            background: '#F8FAFF', border: '1px solid #E2E8F0',
            borderRadius: 14, padding: '16px 20px', marginBottom: 24,
            display: 'flex', gap: 14, alignItems: 'center',
          }}>
            <span style={{ fontSize: 28 }}>🔒</span>
            <div>
              <div style={{ fontWeight: 700, color: '#1E293B', fontSize: 13 }}>
                Secure payments powered by Safepay
              </div>
              <div style={{ color: '#64748B', fontSize: 12, marginTop: 2 }}>
                Accepts Visa & Mastercard from Pakistan and worldwide.
                You will be redirected to Safepay's secure checkout page to complete payment.
                After payment you will be redirected back here automatically.
              </div>
            </div>
          </div>
        )}

        {/* ── Confirm Upgrade Dialog ───────────────────── */}
        {confirming && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: 20,
          }}>
            <div style={{
              background: '#fff', borderRadius: 20,
              padding: 28, maxWidth: 440, width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}>
              <h3 style={{ fontWeight: 800, color: '#1E293B', margin: '0 0 8px' }}>
                Upgrade to {getPlan(confirming).name}?
              </h3>

              {TEST_MODE ? (
                <div style={{
                  background: '#FFFBEB', border: '1px solid #FDE68A',
                  borderRadius: 10, padding: '12px 14px', margin: '12px 0',
                }}>
                  <div style={{ fontWeight: 700, color: '#92400E', fontSize: 13 }}>
                    🧪 Test Mode — No Payment Required
                  </div>
                  <div style={{ color: '#78350F', fontSize: 12, marginTop: 4 }}>
                    Clicking confirm will instantly activate the{' '}
                    <strong>{getPlan(confirming).name}</strong> plan.
                    No real charge will be made.
                  </div>
                </div>
              ) : (
                <div style={{
                  background: '#F0F9FF', border: '1px solid #BAE6FD',
                  borderRadius: 10, padding: '12px 14px', margin: '12px 0',
                }}>
                  <div style={{ fontWeight: 700, color: '#0369A1', fontSize: 13 }}>
                    🔒 Secure Safepay Checkout
                  </div>
                  <div style={{ color: '#0C4A6E', fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
                    You will be redirected to Safepay to pay{' '}
                    <strong>
                      ${cycle === 'yearly'
                        ? getPlan(confirming).price?.yearly
                        : getPlan(confirming).price?.monthly}
                      /{cycle === 'yearly' ? 'year' : 'month'}
                    </strong>.
                    After payment you will return here automatically.
                    Accepts Visa/Mastercard from any country.
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button
                  onClick={() => setConfirming(null)}
                  disabled={upgrading}
                  style={{
                    flex: 1, padding: '12px 0', borderRadius: 10,
                    border: '1.5px solid #E2E8F0', background: 'white',
                    color: '#64748B', fontWeight: 600, cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  onClick={confirmUpgrade}
                  disabled={upgrading}
                  style={{
                    flex: 2, padding: '12px 0', borderRadius: 10,
                    border: 'none',
                    background: getPlan(confirming).gradient,
                    color: '#fff', fontWeight: 700,
                    cursor: upgrading ? 'wait' : 'pointer',
                    opacity: upgrading ? 0.7 : 1,
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 6,
                  }}>
                  {upgrading
                    ? (TEST_MODE ? 'Activating...' : 'Redirecting to Safepay...')
                    : (TEST_MODE
                        ? `⚡ Activate ${getPlan(confirming).name}`
                        : `Pay with Safepay →`)}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Cancel Confirmation Dialog ───────────────── */}
        {cancelConf && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: 20,
          }}>
            <div style={{
              background: '#fff', borderRadius: 20,
              padding: 28, maxWidth: 420, width: '100%',
            }}>
              <h3 style={{ fontWeight: 800, color: '#1E293B', margin: '0 0 8px' }}>
                Cancel Subscription?
              </h3>
              <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 20px', lineHeight: 1.6 }}>
                You will keep access until the end of your billing period.
                After that you will be on the free Trial plan.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setCancelConf(false)} style={{
                  flex: 1, padding: '11px 0', borderRadius: 10,
                  border: '1.5px solid #E2E8F0', background: 'white',
                  color: '#64748B', fontWeight: 600, cursor: 'pointer',
                }}>
                  Keep Plan
                </button>
                <button onClick={handleCancel} style={{
                  flex: 1, padding: '11px 0', borderRadius: 10,
                  border: 'none', background: '#EF4444',
                  color: 'white', fontWeight: 700, cursor: 'pointer',
                }}>
                  Yes, Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}